import { useMemo } from 'react';
import { useBusinessAccessTypes, useBusinessAccessStatuses } from './useBusinessAccessType';

// SAM-581: the dashboard and the invitations screen used to render
// access-type / access-status labels from separate hardcoded lists that
// disagreed on what a given id meant (e.g. id 1 showed as "Residence" in
// one place and "Guest" in another). Which label is "correct" is a
// business decision, not something engineering should hardcode — so this
// hook is the single place that resolves those ids, by loading the real
// `business_access_type` / `business_access_status` records from the
// backend (the same source AccessTypeControl.tsx already trusts) and
// mapping id -> name. A new type/status added in the DB shows up here
// without a frontend change.

export interface AccessOption {
  id: number;
  label: string;
}

export type AccessLabelMap = Record<number, string>;

/**
 * Pure helper: resolve a label for `id` out of `map`. Falls back to the
 * raw id/value (stringified) when the id is unknown or the map has not
 * loaded yet, so filtering and rendering keep working for every existing
 * row even when a label lookup fails.
 */
export const getAccessTypeLabel = (
  map: AccessLabelMap | undefined,
  id: number | string | null | undefined
): string => {
  if (id === null || id === undefined) return '';
  const numericId = typeof id === 'string' ? Number(id) : id;
  const label = map && !Number.isNaN(numericId) ? map[numericId] : undefined;
  return label ?? String(id);
};

type CatalogRow = { id?: number; name?: string };

// SAM-1323 (U39): the http client already unwraps the response, so these hooks hand back the rows themselves
// (AccessTypeControl reads them as an array); `{ data: rows }` is kept for callers that still wrap them.
export const toLabelMap = (catalog: CatalogRow[] | { data?: CatalogRow[] } | undefined): AccessLabelMap => {
  const rows = Array.isArray(catalog) ? catalog : catalog?.data;
  const map: AccessLabelMap = {};
  (rows || []).forEach(row => {
    if (typeof row.id === 'number' && typeof row.name === 'string') {
      map[row.id] = row.name;
    }
  });
  return map;
};

const toOptions = (map: AccessLabelMap): AccessOption[] =>
  Object.entries(map)
    .map(([id, label]) => ({ id: Number(id), label }))
    .sort((a, b) => a.id - b.id);

/**
 * Access-type id -> label map, built from the live `business_access_type`
 * data (the real source of truth). Use `getAccessTypeLabel(map, id)` to
 * resolve a single id with a safe fallback.
 */
export const useAccessTypeLabelMap = (): { map: AccessLabelMap; isLoading: boolean } => {
  const { data, isLoading } = useBusinessAccessTypes();
  const map = useMemo(() => toLabelMap(data), [data]);
  return { map, isLoading };
};

/** Access-status id -> label map, built from the live `business_access_status` data. */
export const useAccessStatusLabelMap = (): { map: AccessLabelMap; isLoading: boolean } => {
  const { data, isLoading } = useBusinessAccessStatuses();
  const map = useMemo(() => toLabelMap(data), [data]);
  return { map, isLoading };
};

/**
 * {value, label} options for access-type filter dropdowns. The option
 * `id` stays the numeric id used for filtering — only the label's source
 * changes (live API instead of a hardcoded list).
 */
export const useAccessTypeOptions = (): { options: AccessOption[]; isLoading: boolean } => {
  const { map, isLoading } = useAccessTypeLabelMap();
  const options = useMemo(() => toOptions(map), [map]);
  return { options, isLoading };
};

/** {value, label} options for access-status filter dropdowns. */
export const useAccessStatusOptions = (): { options: AccessOption[]; isLoading: boolean } => {
  const { map, isLoading } = useAccessStatusLabelMap();
  const options = useMemo(() => toOptions(map), [map]);
  return { options, isLoading };
};
